'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import Box, { BoxProps } from '@mui/material/Box';

import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';

import dayjs from '../../utils/date';

type FormatType = 'since' | 'date' | 'datetime' | 'dateshort';

function formatDate(type: FormatType, date: dayjs.ConfigType, utc?: boolean) {
  const dayjsDate = utc ? dayjs(date).utc() : dayjs(date);

  switch (type) {
    case 'since': {
      return dayjsDate.fromNow();
    }
    case 'date': {
      return dayjsDate.format('MMMM D, YYYY');
    }
    case 'datetime': {
      return dayjsDate.format('MMM D, YYYY h:mm A') + (utc ? ' UTC' : '');
    }
    case 'dateshort': {
      return dayjsDate.format('MMM D, YYYY');
    }
  }
}

type Props = {
  date: dayjs.ConfigType;
  ssr?: boolean;
  sx?: BoxProps['sx'];
  // use this property when component is rendered on the server
  type: FormatType;
};

export function DateTime({ date, ssr, sx, type }: Props) {
  const utcDate = useMemo(() => formatDate('datetime', date, true), [date]);
  const [textContent, setTextContent] = useState(
    // always render UTC time if component is rendered on the server
    ssr ? '' : () => formatDate(type, date, ssr),
  );
  const updateInterval = useRef<number | null>(null);

  useEffect(() => {
    if (type === 'since') {
      setTextContent(formatDate(type, date));
      updateInterval.current = window.setInterval(() => {
        setTextContent(formatDate(type, date));
      }, 1000 * 60);

      return () => {
        if (updateInterval.current) {
          window.clearInterval(updateInterval.current);
        }
      };
    } else {
      setTextContent(formatDate(type, date));
    }
  }, [date, type]);

  return date ? (
    <Tooltip placement="bottom-start" title={utcDate}>
      <Box component="time" dateTime={dayjs(date).utc().toISOString()} sx={sx}>
        {textContent || <Skeleton height="24px" variant="rounded" width={90} />}
      </Box>
    </Tooltip>
  ) : (
    '-'
  );
}
